import React, { useEffect, useContext, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AppSettings } from './../../config/app-settings.js'
import * as Yup from 'yup'
import Loader from '../pages/loader.js'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'

const validationSchema = Yup.object({
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  repeat_password: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
})

function ResetPassword() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { token } = useParams() // ✅ extract token from URL
  const context = useContext(AppSettings)

  // Handle layout changes
  useEffect(() => {
    context.setAppHeaderNone(true)
    context.setAppSidebarNone(true)
    context.setAppContentClass('p-0')

    return () => {
      context.setAppHeaderNone(false)
      context.setAppSidebarNone(false)
      context.setAppContentClass('')
    }
  }, [context])

  // ✅ Formik setup
  const validation = useFormik({
    initialValues: {
      password: '',
      repeat_password: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true)
      setError('')
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}users/reset-password/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // ✅ Send the token as Bearer token
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              password: values.password,
              repeat_password: values.repeat_password
            })
          }
        )

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data?.errors?.[0]?.message || 'Something went wrong')
        }

        toast.success('Password reset successfully!')
        navigate('/login')
      } catch (err) {
        setError(err.message)
        toast.error(err.message)
      } finally {
        setIsLoading(false)
      }
    }
  })

  return (
    <div className='login'>
      <Loader isLoading={isLoading} />
      <div className='login-content'>
        <form onSubmit={validation.handleSubmit}>
          <h1 className='text-center'>Reset Password</h1>

          {/* Password field */}
          <div className='mb-3'>
            <label className='form-label'>
              Password <span className='text-danger'>*</span>
            </label>
            <input
              id='password'
              name='password'
              type='password'
              value={validation.values.password}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              className='form-control form-control-lg bg-white bg-opacity-5'
              placeholder='Enter your new password'
            />
            {validation.touched.password && validation.errors.password && (
              <div className='text-danger mt-1'>{validation.errors.password}</div>
            )}
          </div>

          {/* Confirm password field */}
          <div className='mb-3'>
            <label className='form-label'>
              Confirm Password <span className='text-danger'>*</span>
            </label>
            <input
              id='repeat_password'
              name='repeat_password'
              type='password'
              value={validation.values.repeat_password}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              className='form-control form-control-lg bg-white bg-opacity-5'
              placeholder='Confirm your new password'
            />
            {validation.touched.repeat_password && validation.errors.repeat_password && (
              <div className='text-danger mt-1'>
                {validation.errors.repeat_password}
              </div>
            )}
          </div>

          <button
            type='submit'
            className='btn btn-outline-theme btn-lg d-block w-100 fw-500 mb-3'
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Reset Password'}
          </button>
        </form>

        <Link to='/login' className='text-center'>
          <p>Go Back to Login</p>
        </Link>

        {error && <div className='mt-2 text-center text-sm text-danger'>{error}</div>}
      </div>
    </div>
  )
}

export default ResetPassword
